import { Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { updateCredentialBindings } from '@/api/credential'
import type { ServerCredentialInfo, ServerDeploymentInfo } from '@/api/type-server'
import { CredentialResourceType } from '@/api/type-server'
import { message } from '@/components'
import { Button } from '@/components/base/Button'
import { Checkbox } from '@/components/base/Checkbox'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { EmptyView } from '@/components/EmptyView'
import { canBindCredential } from '@/utils/credential'

interface BindResourcesModalProps {
  credential: ServerCredentialInfo
  onClose: () => void
  onBound: () => void
}

export function BindResourcesModal({ credential, onClose, onBound }: BindResourcesModalProps) {
  const { t } = useTranslation('accessKeys')
  const [deployments, setDeployments] = useState<ServerDeploymentInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const bindingAvailable = canBindCredential(credential)

  // Check if credential is auto-bound (DeploymentClass type=2)
  const isAutoBinding = useMemo(
    () =>
      credential.resources?.some(
        (r) => r.resourceType === CredentialResourceType.DeploymentClass,
      ) ?? false,
    [credential.resources],
  )

  // Get initially bound deployment IDs
  const initialBoundIds = useMemo(() => {
    // Auto-binding (type=2): all deployments are bound
    if (isAutoBinding) {
      return new Set(deployments.map((d) => d.resID))
    }
    // Manual binding (type=1): from credential.resources + deployment.credentials
    const ids = new Set<string>()
    credential.resources?.forEach((r) => {
      if (r.resourceType === CredentialResourceType.Deployment) {
        r.resIDs?.forEach((id) => ids.add(id))
      }
    })
    deployments.forEach((d) => {
      if (d.credentials?.some((c) => c.resID === credential.resID)) {
        ids.add(d.resID)
      }
    })
    return ids
  }, [credential.resources, credential.resID, deployments, isAutoBinding])

  const fetchDeployments = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getDeploymentList({ enabled: true, page: 1, per_page: 100 })
      if (res.code === 0) {
        setDeployments(res.deployments || [])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDeployments()
  }, [fetchDeployments])

  // Initialize selected IDs once deployments are loaded
  useEffect(() => {
    if (deployments.length > 0) {
      setSelectedIds(new Set(initialBoundIds))
    }
  }, [deployments, initialBoundIds])

  const allSelected = deployments.length > 0 && selectedIds.size === deployments.length
  const someSelected = selectedIds.size > 0 && selectedIds.size < deployments.length

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(deployments.map((d) => d.resID)))
    }
  }

  const handleToggle = (resID: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(resID)) {
        next.delete(resID)
      } else {
        next.add(resID)
      }
      return next
    })
  }

  const handleBind = async () => {
    setSubmitting(true)
    try {
      await updateCredentialBindings(credential.resID, 'manual', selectedIds)

      message.success(t('bindModal.success'))
      onBound()
      onClose()
    } catch (error) {
      message.error(error instanceof Error ? error.message : t('errors.requestFailed'))
      onBound()
      await fetchDeployments()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      className="w-[640px] max-h-[calc(100dvh-32px)]"
      open={true}
      onClose={submitting ? undefined : onClose}
    >
      <Modal.Header className="shrink-0">{t('bindModal.title')}</Modal.Header>
      <Modal.Body scrollable showScrollbar className="min-h-0">
        {!bindingAvailable && (
          <p className="text-error t-sm mb-4" role="status">
            {t('errors.bindingUnavailable')}
          </p>
        )}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Loading />
          </div>
        ) : deployments.length === 0 ? (
          <EmptyView content={t('bindModal.noDeployments')} />
        ) : (
          <div className="border border-gray100 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-panel-gray-100">
                  <th className="px-4 py-3 w-[52px]">
                    <Checkbox
                      checked={allSelected}
                      disabled={!bindingAvailable && !allSelected}
                      onChange={handleToggleAll}
                      inputClassName={someSelected ? 'indeterminate' : ''}
                      checkedBgColor="bg-btn-secondary"
                      checkedBorderColor="border-secondary"
                      checkIconColor="after:border-white"
                    />
                  </th>
                  <th className="px-4 py-3 text-left t-sb text-secondary">{t('bindModal.id')}</th>
                  <th className="px-4 py-3 text-left t-sb text-secondary">{t('bindModal.name')}</th>
                  <th className="px-4 py-3 text-left t-sb text-secondary">
                    {t('bindModal.currentAccessKey')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {deployments.map((item) => {
                  const boundCredentials = item.credentials || []
                  const boundNames = boundCredentials.map((c) => c.title).filter(Boolean)
                  const hasBound = boundNames.length > 0
                  return (
                    <tr key={item.resID} className="border-b border-gray100">
                      <td className="px-4 py-4 w-[52px]">
                        <Checkbox
                          checked={selectedIds.has(item.resID)}
                          disabled={!bindingAvailable && !selectedIds.has(item.resID)}
                          onChange={() => handleToggle(item.resID)}
                          checkedBgColor="bg-btn-secondary"
                          checkedBorderColor="border-secondary"
                          checkIconColor="after:border-white"
                        />
                      </td>
                      <td className="px-4 py-4 t-sb text-primary">{item.resID}</td>
                      <td className="px-4 py-4 t-sb text-primary truncate max-w-[200px]">
                        {item.name}
                      </td>
                      <td className="px-4 py-4 t-sb">
                        {boundNames.length > 1 ? (
                          <Tooltip title={boundNames.join(', ')} overlayStyle={{ maxWidth: 240 }}>
                            <span className="text-highlight underline decoration-dotted cursor-default">
                              {t('bindModal.nAccessKeys', { count: boundNames.length })}
                            </span>
                          </Tooltip>
                        ) : hasBound ? (
                          <span className="text-highlight">{boundNames[0]}</span>
                        ) : (
                          <span className="text-tertiary">{t('bindModal.unbound')}</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="shrink-0">
        <Button type="outline" disabled={submitting} onClick={onClose} className="min-w-[88px]">
          {t('bindModal.cancelBtn')}
        </Button>
        <Button
          type="secondary"
          onClick={handleBind}
          disabled={submitting || loading}
          className="min-w-[88px]"
        >
          {t('bindModal.bindBtn')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
